import React, { useEffect, useState } from "react";
import { t } from "../i18n/strings";
import { recordDailyRatingSnapshots, safeLoadRatingHistory } from "../utils/storage";

export default function RatingHistoryGraph({ taxi, lang }) {
  const [entries, setEntries] = useState(() => safeLoadRatingHistory()[taxi.plate] || []);

  useEffect(() => {
    const saved = recordDailyRatingSnapshots([taxi]);
    setEntries(saved[taxi.plate] || []);
  }, [taxi]);

  const width = 360;
  const height = 160;
  const plot = { left: 34, right: 12, top: 12, bottom: 28 };
  const chartWidth = width - plot.left - plot.right;
  const chartHeight = height - plot.top - plot.bottom;
  const points = entries.map((entry, index) => ({
    ...entry,
    x: plot.left + (entries.length === 1 ? chartWidth / 2 : (index / (entries.length - 1)) * chartWidth),
    y: plot.top + ((5 - entry.score) / 5) * chartHeight,
  }));

  return (
    <section style={styles.card} aria-labelledby="rating-history-title">
      <h2 id="rating-history-title" style={styles.title}>{t(lang, "ratingHistoryTitle")}</h2>
      <p style={styles.help}>{t(lang, "ratingHistoryHelp")}</p>
      {!points.length ? <p style={styles.empty}>{t(lang, "ratingHistoryEmpty")}</p> : (
        <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={t(lang, "ratingTrendFor", { plate: taxi.plate })} style={styles.chart}>
          {[0, 1, 2, 3, 4, 5].map((value) => {
            const y = plot.top + ((5 - value) / 5) * chartHeight;
            return <g key={value}><line x1={plot.left} x2={width - plot.right} y1={y} y2={y} stroke="rgba(234,243,246,.12)" /><text x={plot.left - 9} y={y + 4} textAnchor="end" fill="#a7b4cc" fontSize="10">{value}</text></g>;
          })}
          {points.length > 1 && <polyline points={points.map(({ x, y }) => `${x},${y}`).join(" ")} fill="none" stroke="var(--color-accent)" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />}
          {points.map((point) => <circle key={point.date} cx={point.x} cy={point.y} r="4.5" fill="var(--color-accent)" stroke="#0c2138" strokeWidth="2"><title>{`${point.date}: ${point.score.toFixed(1)}/5`}</title></circle>)}
          <text x={plot.left} y={height - 6} fill="#a7b4cc" fontSize="10">{formatChartDate(points[0].date, lang)}</text>
          {points.length > 1 && <text x={width - plot.right} y={height - 6} textAnchor="end" fill="#a7b4cc" fontSize="10">{formatChartDate(points[points.length - 1].date, lang)}</text>}
        </svg>
      )}
    </section>
  );
}

function formatChartDate(date, lang) {
  const [year, month, day] = date.split("-").map(Number);
  try {
    return new Intl.DateTimeFormat(lang, { day: "numeric", month: "short" }).format(new Date(year, month - 1, day));
  } catch {
    return date;
  }
}

const styles = {
  card: { width: "100%", maxWidth: 400, marginTop: 18, padding: "16px 14px 10px", borderRadius: 20, background: "var(--panel)", boxShadow: "var(--shadow-sm)", textAlign: "left" },
  title: { margin: 0, fontFamily: "var(--font-heading)", fontSize: 18 },
  help: { margin: "4px 0 0", color: "var(--color-neutral-700)", fontSize: 11, lineHeight: 1.35 },
  empty: { margin: "24px 8px 18px", textAlign: "center", color: "var(--color-neutral-700)", fontSize: 13 },
  chart: { display: "block", width: "100%", height: "auto", marginTop: 10, overflow: "visible" },
};
